
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/base/SEOHead';

export default function VideosPage() {
  const { t } = useTranslation();
  const [currentlyPlaying, setCurrentlyPlaying] = useState<string | null>(null);

  const featuredVideos = [
    {
      id: 'echoes-of-the-dead-video',
      title: 'Echoes of the Dead - Official Music Video',
      youtubeId: 'TXjtSDHlJtQ',
      thumbnail: 'https://img.youtube.com/vi/TXjtSDHlJtQ/maxresdefault.jpg',
      duration: '4:28',
      description: 'The haunting official music video for Echoes of the Dead, featuring dark electronic soundscapes and supernatural visuals.',
      views: '1.2K',
      isNew: true
    },
    {
      id: 'shadows-spirits-video',
      title: 'Shadows & Spirits - Official Music Video',
      youtubeId: 'BGTHQI1mclg',
      thumbnail: 'https://img.youtube.com/vi/BGTHQI1mclg/maxresdefault.jpg',
      duration: '3:52',
      description: 'The mysterious official music video for Shadows & Spirits, showcasing ghostly atmospheres and dark electronic artistry.',
      views: '890',
      isNew: true
    },
    {
      id: 'break-the-ground-video',
      title: 'Break the Ground - Official Music Video',
      youtubeId: 'AHNZfMUWP9c',
      thumbnail: 'https://img.youtube.com/vi/AHNZfMUWP9c/maxresdefault.jpg',
      duration: '3:45',
      description: 'The official music video for Break the Ground, featuring stunning visuals and high-energy EDM production.',
      views: '2.5K'
    },
    {
      id: 'running-free-video',
      title: 'Running Free - Official Music Video',
      youtubeId: 'WjtccVP-wEM',
      thumbnail: 'https://img.youtube.com/vi/WjtccVP-wEM/maxresdefault.jpg',
      duration: '4:12',
      description: 'The official music video for Running Free, showcasing dynamic visuals and electronic music artistry.',
      views: '1.8K'
    },
    {
      id: 'when-we-loved-video',
      title: 'When We Loved - Official Music Video',
      youtubeId: '2Mpyq3tTcbI',
      thumbnail: 'https://img.youtube.com/vi/2Mpyq3tTcbI/maxresdefault.jpg',
      duration: '4:32',
      description: 'An emotional music video for When We Loved, showcasing the romantic side of electronic music.',
      views: '3.2K'
    },
    {
      id: 'firestarter-hearts-video',
      title: 'Firestarter Hearts - Official Music Video',
      youtubeId: 'eIS-uXyxd3I',
      thumbnail: 'https://img.youtube.com/vi/eIS-uXyxd3I/maxresdefault.jpg',
      duration: '4:05',
      description: 'The passionate music video for Firestarter Hearts, an EDM love anthem with captivating visuals.',
      views: '2.1K'
    }
  ];

  const handleVideoClick = (videoId: string) => {
    if (currentlyPlaying === videoId) {
      setCurrentlyPlaying(null);
    } else {
      setCurrentlyPlaying(videoId);
    }
  };

  return (
    <>
      <SEOHead 
        title={t('seo.videos.title')}
        description={t('seo.videos.description')}
      />
      <div className="min-h-screen bg-black text-white">
        <Navigation />
        
        {/* Hero Section */}
        <section 
          className="py-24 bg-gradient-to-br from-gray-900 via-black to-purple-900 text-white relative overflow-hidden"
          style={{
            backgroundImage: `linear-gradient(rgba(0,0,0,0.7), rgba(0,0,0,0.5)), url('https://public.readdy.ai/ai/img_res/07dc9bbc4fff964c8b78afbb1bac365e.jpg')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 
              className="text-6xl md:text-8xl font-black mb-8 tracking-tight"
              style={{ fontFamily: '"Orbitron", sans-serif' }}
            >
              <span className="bg-gradient-to-r from-cyan-400 via-purple-5 00 bg-clip-text text-transparent">
                {t('videos.hero_title')}
              </span>
            </h1>
            <p 
              className="text-2xl md:text-3xl text-gray-300 max-w-4xl mx-auto mb-12 font-semibold"
              style={{ fontFamily: '"Exo 2", sans-serif' }}
            >
              {t('videos.hero_subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <a
                href="https://www.youtube.com/@DJJeanClaudeBastos"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-red-600 hover:bg-red-700 text-white px-12 py-4 rounded-full font-bold text-xl transition-all transform hover:scale-105 cursor-pointer whitespace-nowrap shadow-2xl"
                style={{ fontFamily: '"Rajdhani", sans-serif' }}
              >
                <i className="ri-youtube-line mr-3 text-2xl"></i>
                {t('videos.subscribe_youtube')}
              </a>
            </div>
          </div>
        </section>

        {/* Featured Videos */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-20">
              <h2 
                className="text-5xl md:text-6xl font-black text-gray-900 mb-6 tracking-tight"
                style={{ fontFamily: '"Orbitron", sans-serif' }}
              >
                <span className="bg-gradient-to-r from-purple-600 to-cyan-500 bg-clip-text text-transparent">
                  {t('videos.music_videos')}
                </span>
              </h2>
              <p 
                className="text-2xl text-gray-600 max-w-3xl mx-auto font-semibold"
                style={{ fontFamily: '"Exo 2", sans-serif' }}
              >
                {t('videos.official_content')}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 max-w-6xl mx-auto">
              {featuredVideos.map((video) => (
                <div
                  key={video.id}
                  className="bg-white rounded-2xl shadow-2xl overflow-hidden hover:shadow-3xl transition-all transform hover:scale-105 cursor-pointer border border-gray-100 relative"
                >
                  {video.isNew && (
                    <div className="absolute top-4 left-4 bg-gradient-to-r from-red-500 to-purple-600 text-white px-3 py-1 rounded-full text-sm font-bold z-10">
                      NEW
                    </div>
                  )}
                  {currentlyPlaying === video.id ? (
                    <div className="aspect-video w-full">
                      <iframe
                        width="100%"
                        height="100%"
                        src={`https://www.youtube.com/embed/${video.youtubeId}?autoplay=1`}
                        title={video.title}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        className="rounded-t-2xl"
                      ></iframe>
                    </div>
                  ) : (
                    <div className="relative aspect-video">
                      <img
                        src={video.thumbnail}
                        alt={video.title}
                        className="w-full h-full object-cover object-center"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent hover:from-black/80 transition-all flex items-center justify-center opacity-0 hover:opacity-100">
                        <button
                          onClick={() => handleVideoClick(video.id)}
                          className="bg-red-600 hover:bg-red-700 text-white w-20 h-20 rounded-full flex items-center justify-center transition-all transform hover:scale-110 cursor-pointer shadow-2xl"
                        >
                          <i className="ri-play-fill text-3xl ml-1"></i>
                        </button>
                      </div>
                      <div className="absolute bottom-4 right-4 bg-black/80 text-white px-3 py-1 rounded-lg text-sm font-bold">
                        {video.duration}
                      </div>
                      <div className="absolute top-4 right-4 bg-red-600 text-white px-3 py-1 rounded-lg text-sm font-bold">
                        {video.views} {t('videos.views')}
                      </div>
                    </div>
                  )}
                  
                  <div className="p-8">
                    <h3 
                      className="text-2xl font-black text-gray-900 mb-4 tracking-wide leading-tight"
                      style={{ fontFamily: '"Rajdhani", sans-serif' }}
                    >
                      {video.title.toUpperCase()}
                    </h3>
                    <p 
                      className="text-gray-600 mb-6 font-semibold leading-relaxed"
                      style={{ fontFamily: '"Exo 2", sans-serif' }}
                    >
                      {video.description}
                    </p>
                    <div className="flex items-center justify-between">
                      <button
                        onClick={() => handleVideoClick(video.id)}
                        className={`${
                          currentlyPlaying === video.id
                            ? 'text-red-500'
                            : 'text-gray-400'
                        } hover:text-red-500 transition-colors cursor-pointer transform hover:scale-125`}
                      >
                        <i
                          className={`text-3xl ${
                            currentlyPlaying === video.id
                              ? 'ri-stop-circle-fill'
                              : 'ri-play-circle-line'
                          }`}
                        ></i>
                      </button>
                      <a
                        href={`https://www.youtube.com/watch?v=${video.youtubeId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-bold text-sm transition-all cursor-pointer whitespace-nowrap flex items-center"
                        style={{ fontFamily: '"Rajdhani", sans-serif' }}
                      >
                        <i className="ri-youtube-line mr-2 text-lg"></i>
                        {t('videos.watch_youtube')}
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Call to Action */}
        <section className="py-24 bg-gradient-to-r from-red-600 via-purple-600 to-cyan-500">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 
              className="text-5xl md:text-6xl font-black text-white mb-8 tracking-tight"
              style={{ fontFamily: '"Orbitron", sans-serif' }}
            >
              {t('videos.stay_connected')}
            </h2>
            <p 
              className="text-2xl text-white/90 mb-12 max-w-3xl mx-auto font-bold"
              style={{ fontFamily: '"Exo 2", sans-serif' }}
            >
              {t('videos.subscribe_message')}
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <a
                href="https://www.youtube.com/@DJJeanClaudeBastos"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white text-red-600 hover:bg-gray-100 px-12 py-5 rounded-full font-black text-xl transition-all transform hover:scale-105 cursor-pointer whitespace-nowrap shadow-2xl"
                style={{ fontFamily: '"Rajdhani", sans-serif' }}
              >
                <i className="ri-youtube-line mr-3 text-2xl"></i>
                {t('videos.subscribe_now')}
              </a>
              <a
                href="/music"
                className="border-4 border-white text-white hover:bg-white hover:text-purple-600 px-12 py-5 rounded-full font-black text-xl transition-all transform hover:scale-105 cursor-pointer whitespace-nowrap shadow-2xl"
                style={{ fontFamily: '"Rajdhani", sans-serif' }}
              >
                <i className="ri-music-line mr-3 text-2xl"></i>
                {t('videos.listen_music')}
              </a>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
